import { KeyManager } from '../security/KeyManager';

/**
 * Backup metadata
 */
export interface BackupMetadata {
  version: number;
  createdAt: string;
  checksum: string;
  networks: string[];
}

/**
 * Encrypted backup payload
 */
export interface EncryptedBackup {
  metadata: BackupMetadata;
  encryptedSeed: string;
  salt: string;
  iv: string;
}

/**
 * Backup Service
 * Handles encrypted seed backup to S3 and recovery
 */
export class BackupService {
  private apiUrl: string;
  private accessToken: string;

  constructor(apiUrl: string, accessToken: string) {
    this.apiUrl = apiUrl;
    this.accessToken = accessToken;
  }

  /**
   * Export encrypted seed to S3
   */
  async exportSeedEncrypted(
    seed: string,
    password: string,
    networks: string[] = []
  ): Promise<{ backupId: string; checksum: string }> {
    // Encrypt the seed
    const encryptedSeed = await KeyManager.encryptSeed(seed, password);

    // Generate checksum for integrity verification
    const checksum = await this.generateChecksum(encryptedSeed);

    // Create backup payload
    const backup: EncryptedBackup = {
      metadata: {
        version: 1,
        createdAt: new Date().toISOString(),
        checksum,
        networks,
      },
      encryptedSeed,
      salt: this.extractSalt(encryptedSeed),
      iv: this.extractIV(encryptedSeed),
    };

    // Upload to API (which stores in S3)
    const response = await fetch(`${this.apiUrl}/backups`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: JSON.stringify({ backup: JSON.stringify(backup) }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create backup');
    }

    const result = await response.json();

    return {
      backupId: result.backupId,
      checksum,
    };
  }

  /**
   * Import encrypted seed from S3
   */
  async importSeedEncrypted(password: string): Promise<string> {
    // Fetch backup from API
    const response = await fetch(`${this.apiUrl}/backups/latest`, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No backup found');
      }
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch backup');
    }

    const result = await response.json();
    const backup: EncryptedBackup = JSON.parse(result.backup);

    // Verify integrity
    const checksum = await this.generateChecksum(backup.encryptedSeed);
    if (checksum !== backup.metadata.checksum) {
      throw new Error('Backup integrity check failed');
    }

    // Decrypt the seed
    const seed = await KeyManager.decryptSeed(backup.encryptedSeed, password);

    return seed;
  }

  /**
   * List all backups
   */
  async listBackups(): Promise<BackupMetadata[]> {
    const response = await fetch(`${this.apiUrl}/backups`, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to list backups');
    }

    const result = await response.json();
    return result.backups.map((b: any) => b.metadata);
  }

  /**
   * Delete a backup
   */
  async deleteBackup(backupId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/backups/${backupId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete backup');
    }
  }

  /**
   * Verify backup can be decrypted
   */
  async verifyBackup(password: string): Promise<boolean> {
    try {
      await this.importSeedEncrypted(password);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Generate SHA-256 checksum
   */
  private async generateChecksum(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);

    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Extract salt from encrypted data (first 32 hex chars)
   */
  private extractSalt(encryptedData: string): string {
    return encryptedData.substring(0, 32);
  }

  /**
   * Extract IV from encrypted data (chars 32-56)
   */
  private extractIV(encryptedData: string): string {
    return encryptedData.substring(32, 56);
  }
}

/**
 * Backup routes handler (for API)
 */
export interface BackupRoutesConfig {
  s3Bucket: string;
  s3Prefix: string;
}
